import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import ActionCreator from "../../../store/actions/index";
import { List, makeStyles, useTheme, Typography, Button } from "@material-ui/core";
import ListItem from "./RouteItem";
import { Scrollbars } from "react-custom-scrollbars";
import moment from 'moment-timezone';
import CurrentRoute from './RouteListElement/CurrentRoute';
import Route from './RouteListElement/Route';
import Refuel from "./RouteListElement/Refuel";
import { themeTypeToBool } from '../../../utils/helpers';
import Zone from './RouteListElement/Zone';
import Warning from "./RouteListElement/Warning";
import {
  typeRoute, typeZone, typeWarning, typeRefuel, displayDateTimeFormat,
  displayTimeWithSecondsFormat, typeLayover, fullDayUnix
} from '../../../utils/staticData';
import { TooltipRight } from "../../Common/CustomTooltips";
import t from "../../../languages/pl";
import Layover from './RouteListElement/Layover';
import guid from '../../../utils/guid';

const useStyles = makeStyles(theme => ({
  list: {
    maxHeight: 600,
    overflow: "auto",
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  noRoutes: {
    textAlign: "center",
    padding: theme.spacing(1)
  },
  noRoutesButton: {
    padding: theme.spacing(1),
    paddingBottom: 0,
    display: "flex",
    justifyContent: 'center'
  },
  container: {
    display: "flex",
    flexDirection: "column",
    justifyContent: 'center',
    width: "100%"
  }
}));

const getDurationFromUnix = (duration) => {
  let divide = duration / fullDayUnix;
  if (divide >= 1)
    return `${Math.floor(divide)} ${divide >= 2 ? t.days : t.day} ${moment.utc(duration % fullDayUnix, 'X').format(displayTimeWithSecondsFormat)}`;
  return moment.utc(duration, 'X').format(displayTimeWithSecondsFormat);
};

function RouteList(props) {
  const {
    onMounted = null,
    routes,
    showRoute,
    showZone,
    showRefuel,
    showWarning,
    onGetLastRoutes,
    currentRoute,
    showCurrentRoute
  } = props;
  const theme = useTheme();
  const classes = useStyles(theme);
  const scrollbars = useRef(null);
  const [visibleRoutes, setVisibleRoutes] = useState([]);
  const [routeCount, setCount] = useState(15);

  useEffect(() => {
    if (onMounted)
      onMounted();
  }, []);

  useEffect(() => {
    if (!routes)
      return;
    let possibleElements = routes.filter(route => checkFilters(route));
    setVisibleRoutes(possibleElements);
  }, [routes, routeCount, showRoute, showZone, showRefuel, showWarning]);

  const handleBottomReached = (e) => {
    if (e.top >= 1)
      setCount(count => count + 5);
  };

  const handleClick = (id, selected) => {
    selected ? props.deselectRoute(id) : props.selectRoute(id);
  };

  const checkFilters = (route) => {
    switch (route.type) {
      case typeRoute:
        return showRoute && route;
      case typeLayover:
        return showRoute && route;
      case typeZone:
        return showZone && route;
      case typeRefuel:
        return showRefuel && route;
      case typeWarning:
        return showWarning && route;
      default:
        return undefined;
    }
  };

  const showData = () => {
    if (routes == null)
      return (
        <Typography
          variant="subtitle1"
          className={classes.noRoutes}
        >
          {t.searching}
        </Typography>
      );
    else if (!Array.isArray(routes) || routes.length === 0)
      return (
        <div className={classes.noRoutesButton}>
          <TooltipRight title={t.searchLastRoutes}>
            <Button
              variant="outlined"
              onClick={onGetLastRoutes}
            >
              {t.noDataInSpecifiedTime}
            </Button>
          </TooltipRight>
        </div>
      );
    else if (visibleRoutes.length === 0)
      return (
        <Typography
          variant="subtitle1"
          className={classes.noRoutes}
        >
          {t.noDataForSpecifiedCriteria}
        </Typography>
      );

    return listRoutes();
  };

  const showCurrrentRoute = () => {
    if (currentRoute && currentRoute.data.length > 0) {
      return CurrentRouteType(currentRoute.data);
    }
  };

  const CurrentRouteType = data => (
    <React.Fragment key={guid()}>
      <ListItem
        selected={showCurrentRoute}
        background={themeTypeToBool(theme.palette.type) ? "limeGreen" : "lime"}
        onCheckboxClick={() => props.changeShowingCurrentRoute(!showCurrentRoute)}
      >
        <CurrentRoute
          key={guid()}
          name={data[0].kierowca}
          startTime={moment.unix(data[0].data_od).format(displayDateTimeFormat)}
          //finishTime={moment.unix(data.data_do).format(displayDateTimeFormat)}
          distance={data[0].dystans}
          maxSpeed={data[0].predkosc_max}
          averageSpeed={data[0].predkosc_sr}
        />
      </ListItem>
    </React.Fragment>
  );

  const RouteType = data => (
    <React.Fragment key={data.id}>
      <ListItem
        selected={data.selected}
        background={themeTypeToBool(theme.palette.type) ? "#d2d2d6" : "#C6C6E8"}
        onCheckboxClick={handleClick.bind(this, data.id, data.selected)}
      >
        <Route
          key={data.id}
          name={data.kierowca}
          startTime={moment.unix(data.data_od).format(displayDateTimeFormat)}
          finishTime={moment.unix(data.data_do).format(displayDateTimeFormat)}
          distance={data.dystans}
          maxSpeed={data.predkosc_max}
          averageSpeed={data.predkosc_sr}
        />
      </ListItem>
    </React.Fragment>
  );

  const RefuelItem = data => (
    <ListItem
      key={data.id}
      selected={data.selected}
      background={themeTypeToBool(theme.palette.type) ? "#42bd9c" : "#00AF80"}
      onCheckboxClick={handleClick.bind(this, data.id, data.selected)}
    >
      <Refuel
        time={moment.unix(data.data).format(displayDateTimeFormat)}
        amount={data.litry}
      />
    </ListItem>
  );

  const ZoneItem = data => (
    <ListItem
      key={data.id}
      selected={data.selected}
      background={themeTypeToBool(theme.palette.type) ? "#65cde4" : "#65cde4"}
      onCheckboxClick={handleClick.bind(this, data.id, data.selected)}
    >
      <Zone
        startTime={moment.unix(data.data_od).format(displayDateTimeFormat)}
        finishTime={moment.unix(data.data_do).format(displayDateTimeFormat)}
        timeOutsideZone={getDurationFromUnix(data.czas_poza_rejonem)}
        distance={data.km_poza_rejonem}
      />
    </ListItem>
  );

  const WarningItem = data => (
    <ListItem
      key={data.id}
      selected={data.selected}
      background={themeTypeToBool(theme.palette.type) ? "#F4D3B4" : "#F4D3B4"}
      onCheckboxClick={handleClick.bind(this, data.id, data.selected)}
    >
      <Warning
        time={moment.unix(data.data).format(displayDateTimeFormat)}
        type={data.typ}
        description={data.zdarzenie}
      />
    </ListItem>
  );

  const LayoverItem = data => (
    <ListItem
      key={data.id}
      background={themeTypeToBool(theme.palette.type) ? "#e1e0e2" : "#e1e0e2"}
    >
      <Layover duration={getDurationFromUnix(data.czas_postoju)}/>
    </ListItem>
  );

  const listRoutes = () => {
    return visibleRoutes.map(data => {
      switch (data.type) {
        case typeRoute:
          return RouteType(data);
        case typeRefuel:
          return RefuelItem(data);
        case typeZone:
          return ZoneItem(data);
        case typeWarning:
          return WarningItem(data);
        case typeLayover:
          return LayoverItem(data);
        default:
          return <Typography variant="subtitle1">{t.unexpectedError}</Typography>;
      }
    });
  };

  return (
    <List component="div" disablePadding className={classes.list}>
      <Scrollbars
        ref={scrollbars}
        autoHeight
        autoHeightMax={550}
        onScrollFrame={handleBottomReached}
      >
        {showCurrrentRoute()}
        {showData()}
      </Scrollbars>
    </List>
  );
}

const mapStateToProps = state => {
  return {
    selectedRoutes: state.mapRoutesReducer.selectedRoutes,
    currentRoute: state.mapRoutesReducer.currentRoute,
    showCurrentRoute: state.mapRoutesReducer.showCurrentRoute,
    showRoute: state.filterReducer.drive,
    showRefuel: state.filterReducer.refuel,
    showZone: state.filterReducer.outOfZone,
    showWarning: state.filterReducer.warnings,
  };
};

const mapDispatchToProps = dispatch => {
  return bindActionCreators(ActionCreator, dispatch);
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(RouteList);

RouteList.propTypes = {
  onMounted: PropTypes.func,
  onGetLastRoutes: PropTypes.func,
  //redux
  routes: PropTypes.array,
  currentRoute: PropTypes.object,
  showCurrentRoute: PropTypes.bool,
  showRoute: PropTypes.bool,
  showZone: PropTypes.bool,
  showRefuel: PropTypes.bool,
  showWarning: PropTypes.bool,
  selectRoute: PropTypes.func,
  deselectRoute: PropTypes.func,
  changeShowingCurrentRoute: PropTypes.func,
};
